import { useState, useEffect } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function MobileInstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIos || isAndroid;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (!isMobile || dismissed || isStandalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // On iOS (no beforeinstallprompt) or as fallback, show banner after 3 sec
    const timer = setTimeout(() => {
      if (!isStandalone && !dismissed) setShow(true);
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const result = await deferredPrompt.userChoice;
        if (result.outcome === 'accepted') {
          setShow(false);
          sessionStorage.setItem('pwa-banner-dismissed', 'true');
        }
        setDeferredPrompt(null);
      } catch {
        // fallback to manual instructions
        setShowIosHelp(true);
      }
    } else {
      // No prompt available (iOS Safari or Android browser without PWA criteria met)
      setShowIosHelp(true);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!show) return null;

  return (
    <>
      <div data-testid="mobile-install-banner"
        className="fixed top-0 left-0 right-0 z-[60] px-4 py-3 flex items-center gap-3 animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg-dark)', borderBottom: '1px solid rgba(96,165,250,0.2)' }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(37,99,235,0.15)' }}>
          <Download className="w-4.5 h-4.5" style={{ color: 'var(--accent-light)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-on-dark)', fontFamily: 'var(--font-body)' }}>
            Installa Quintessenza
          </p>
          <p className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
            Accedi velocemente dalla home del tuo telefono
          </p>
        </div>
        <Button onClick={handleInstall} size="sm" data-testid="install-app-btn"
          className="rounded-sm text-xs flex-shrink-0 cta-glow"
          style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
          {isIos ? 'Aggiungi' : 'Installa'}
        </Button>
        <button onClick={handleDismiss} className="p-1 flex-shrink-0" data-testid="dismiss-install-banner" aria-label="Chiudi">
          <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
        </button>
      </div>

      {showIosHelp && (
        <div data-testid="install-help-modal"
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 animate-fade-in"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowIosHelp(false)}>
          <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ backgroundColor: '#fff' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>
                  Aggiungi alla schermata Home
                </h3>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {isIos ? 'Su iPhone / iPad (Safari)' : 'Sul tuo dispositivo'}
                </p>
              </div>
              <button onClick={() => setShowIosHelp(false)} className="p-1" data-testid="close-install-help" aria-label="Chiudi">
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            {isIos ? (
              <ol className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>1</span>
                  <span className="flex items-center gap-1">
                    Tocca il pulsante <Share className="w-4 h-4 inline" /> <strong>Condividi</strong> nella barra di Safari
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>2</span>
                  <span className="flex items-center gap-1">
                    Scorri e seleziona <Plus className="w-4 h-4 inline" /> <strong>Aggiungi alla schermata Home</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>3</span>
                  <span>Conferma con <strong>Aggiungi</strong> in alto a destra</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>1</span>
                  <span>Apri il menu del browser (i tre puntini in alto a destra)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>2</span>
                  <span>Seleziona <strong>Installa app</strong> oppure <strong>Aggiungi a schermata Home</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: '#3B82F6' }}>3</span>
                  <span>Conferma con <strong>Installa</strong></span>
                </li>
              </ol>
            )}

            <Button onClick={() => setShowIosHelp(false)} data-testid="install-help-ok-btn"
              className="w-full mt-5 rounded-sm cta-glow"
              style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
              Ho capito
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
